import { useState, useEffect } from 'react';
import { SellOnlineSwitch, useStoreFeature } from '../components/commerce/SellOnline';
import OfferingPricingEditor from '../components/commerce/OfferingPricingEditor';
import SchedulingFields, { schedulingDefaults, schedulingBody } from '../components/commerce/SchedulingFields';
import PauseRulesFields, { pauseRulesDefaults, pauseRulesBody } from '../components/commerce/PauseRulesFields';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Plus, Search, Pencil, ToggleLeft, ToggleRight, ShoppingBag, X, Loader2 } from 'lucide-react';
import { useUserContext } from '../contexts/UserContext';

interface GymClass { id: string; name: string; }

interface Service {
  id: string;
  sellOnline?: boolean;
  gymId: string;
  name: string;
  category: string | null;
  pricingType: string;
  defaultPrice: number;
  durationDays: number | null;
  sessionCount: number | null;
  trainerRequired: boolean;
  gstPercent: number;
  description: string | null;
  isActive: boolean;
  serviceType: string;
  classId: string | null;
  gymClass: GymClass | null;
  createdAt: string;
  requiresMembership?: boolean;
  /** Pause rules for the pack (null = cannot be paused). */
  pauseRules?: any;
  /** What it grants: sessions per week/month (a training plan) or in total, and who delivers it. */
  rule?: { period: 'week' | 'month' | null; trainerMode: string; trainerLocked: boolean; scheduling?: string; slotsPerWeek?: number | null; slotDurationMin?: number | null } | null;
}

const PRICING_TYPES = ['fixed', 'per_session', 'per_day', 'custom'];
const SERVICE_TYPES = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'CLASS_SESSION', label: 'Drop-in Class Session' },
  { value: 'CLASS_PACK', label: 'Sessions (pack or training plan)' },
];

function ServiceModal({
  service,
  gymId: propGymId,
  hasClassScheduling,
  onClose,
  onSaved
}: {
  service: Partial<Service> | null;
  gymId: string;
  hasClassScheduling: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!service?.id;
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState<GymClass[]>([]);
  const [form, setForm] = useState({
    name: service?.name || '',
    category: service?.category || '',
    pricingType: service?.pricingType || 'fixed',
    defaultPrice: service?.defaultPrice?.toString() || '',
    durationDays: service?.durationDays?.toString() || '',
    sessionCount: service?.sessionCount?.toString() || '',
    trainerRequired: service?.trainerRequired || false,
    gstPercent: service?.gstPercent?.toString() || '18',
    description: service?.description || '',
    serviceType: service?.serviceType || 'STANDARD',
    classId: service?.classId || '',
    period: service?.rule?.period ?? '',
    trainerMode: service?.rule?.trainerMode ?? 'any',
    trainerLocked: service?.rule?.trainerLocked ?? false,
    sched: schedulingDefaults(service?.rule),
    requiresMembership: service?.requiresMembership ?? false,
    pause: pauseRulesDefaults(service?.pauseRules),
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const effectiveGymId = service?.gymId || propGymId;
    if (!effectiveGymId) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${API_BASE_URL}/api/classes?gymId=${effectiveGymId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      }).then(r => r.json()).then(d => setClasses(d.data || [])).catch(() => {});
    });
  }, [service?.gymId, propGymId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.defaultPrice) return toast.error('Price is required');
    if (form.serviceType !== 'STANDARD' && !form.classId) {
      return toast.error('Please select a class for this service type');
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const body: any = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        pricingType: form.pricingType,
        defaultPrice: parseFloat(form.defaultPrice),
        durationDays: form.durationDays ? parseInt(form.durationDays) : null,
        sessionCount: form.sessionCount ? parseInt(form.sessionCount) : null,
        trainerRequired: form.trainerRequired,
        gstPercent: parseFloat(form.gstPercent) || 0,
        description: form.description.trim() || null,
        serviceType: form.serviceType,
        classId: form.serviceType !== 'STANDARD' ? (form.classId || null) : null,
        requiresMembership: form.requiresMembership,
        ...(form.serviceType === 'CLASS_PACK' && {
          period: form.period || null,
          trainerMode: form.trainerMode,
          trainerLocked: form.trainerMode !== 'any' && form.trainerLocked,
          ...schedulingBody(form.trainerMode === 'any' ? { ...form.sched, scheduling: 'on_demand' } : form.sched),
          pauseRules: pauseRulesBody(form.pause),
        }),
      };

      const url = isEdit
        ? `${API_BASE_URL}/api/services/${service!.id}`
        : `${API_BASE_URL}/api/services`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = isEdit ? body : { ...body, gymId: service?.gymId || propGymId };

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed');
      toast.success(isEdit ? 'Service updated' : 'Service created');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const isClassLinked = form.serviceType !== 'STANDARD';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1001] p-0 sm:p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[90dvh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-violet-600 to-purple-700 p-5 text-white shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black">{isEdit ? 'Edit Service' : 'New Service'}</h2>
              <p className="text-white/60 text-xs">Catalog Item</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 bg-white/10 hover:bg-red-500/80 rounded-xl border border-white/20 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label-xs">Service Type</label>
              <select value={form.serviceType} onChange={e => set('serviceType', e.target.value)} className="field-input">
                {SERVICE_TYPES.filter(t => hasClassScheduling || t.value === 'STANDARD').map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {isClassLinked && (
              <div className="col-span-2">
                <label className="label-xs">Linked Class *</label>
                <select value={form.classId} onChange={e => set('classId', e.target.value)} className="field-input" required>
                  <option value="">— Select class —</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">
                  {form.serviceType === 'CLASS_PACK'
                    ? 'Credits from this pack can only be used for this class.'
                    : 'Pay-per-session pricing for this class.'}
                </p>
              </div>
            )}
            <div className="col-span-2">
              <label className="label-xs">Service Name *</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. 10-Class Yoga Pack" className="field-input" required />
            </div>
            <div>
              <label className="label-xs">Category</label>
              <input type="text" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Cardio, Strength" className="field-input" />
            </div>
            <div>
              <label className="label-xs">Pricing Type</label>
              <select value={form.pricingType} onChange={e => set('pricingType', e.target.value)} className="field-input">
                {PRICING_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label-xs">Price (₹) *</label>
              <input type="number" min={0} value={form.defaultPrice} onChange={e => set('defaultPrice', e.target.value)} placeholder="0.00" className="field-input" required />
            </div>
            <div>
              <label className="label-xs">GST %</label>
              <input type="number" min={0} max={100} value={form.gstPercent} onChange={e => set('gstPercent', e.target.value)} className="field-input" />
            </div>
            {form.serviceType === 'CLASS_PACK' && (
              <>
                <div>
                  <label className="label-xs">Sessions *</label>
                  <input type="number" min={1} value={form.sessionCount} onChange={e => set('sessionCount', e.target.value)} placeholder="e.g. 10" className="field-input" required />
                </div>
                <div>
                  <label className="label-xs">Per</label>
                  <select value={form.period} onChange={e => set('period', e.target.value)} className="field-input">
                    <option value="">In total (a pack)</option>
                    <option value="week">Week (a training plan)</option>
                    <option value="month">Month (a training plan)</option>
                  </select>
                </div>
                <div>
                  <label className="label-xs">Valid for (days){form.period ? ' *' : ''}</label>
                  <input type="number" min={1} value={form.durationDays} onChange={e => set('durationDays', e.target.value)} placeholder={form.period ? 'e.g. 30' : 'Optional — never expires'} className="field-input" required={!!form.period} />
                </div>
                <div>
                  <label className="label-xs">Who delivers it</label>
                  <select value={form.trainerMode} onChange={e => set('trainerMode', e.target.value)} className="field-input">
                    <option value="any">Anyone free</option>
                    <option value="member_picks">Member picks a coach</option>
                    <option value="gym_assigns">Gym assigns a coach</option>
                  </select>
                </div>
                {form.trainerMode !== 'any' && (
                  <label className="col-span-2 flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.trainerLocked} onChange={e => set('trainerLocked', e.target.checked)} className="w-4 h-4 mt-0.5 accent-violet-600" />
                    <span>
                      <span className="block text-sm font-bold text-gray-700">Book only with that coach</span>
                      <span className="block text-[11px] text-gray-400">
                        {form.trainerMode === 'member_picks'
                          ? 'Members must choose a coach when they buy. Off: “Any available” is offered too.'
                          : 'Sessions go to the coach you assign on the member’s profile.'}
                      </span>
                    </span>
                  </label>
                )}
                <SchedulingFields value={form.sched} trainerMode={form.trainerMode} onChange={v => set('sched', v)} />
                <label className="col-span-2 flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.requiresMembership} onChange={e => set('requiresMembership', e.target.checked)} className="w-4 h-4 mt-0.5 accent-violet-600" />
                  <span>
                    <span className="block text-sm font-bold text-gray-700">Only for members with an active plan</span>
                    <span className="block text-[11px] text-gray-400">An add-on: the app asks for a plan first.</span>
                  </span>
                </label>
                <PauseRulesFields value={form.pause} onChange={v => set('pause', v)} />
                {isEdit && form.trainerMode !== 'any' && service?.rule?.trainerMode === form.trainerMode && (
                  <div className="col-span-2 rounded-2xl border border-gray-100 bg-gray-50/60 p-4 space-y-2">
                    <div className="label-xs" style={{ marginBottom: 0 }}>Price by coach level</div>
                    <OfferingPricingEditor ownerType="service" ownerId={service!.id!} />
                  </div>
                )}
              </>
            )}
            {form.serviceType === 'STANDARD' && (
              <>
                <div>
                  <label className="label-xs">Duration (days)</label>
                  <input type="number" min={0} value={form.durationDays} onChange={e => set('durationDays', e.target.value)} placeholder="Optional" className="field-input" />
                </div>
                <div>
                  <label className="label-xs">Sessions</label>
                  <input type="number" min={0} value={form.sessionCount} onChange={e => set('sessionCount', e.target.value)} placeholder="Optional" className="field-input" />
                </div>
              </>
            )}
            <div className="col-span-2">
              <label className="label-xs">Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="field-input resize-none" placeholder="Brief description..." />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <input type="checkbox" id="trainerReq" checked={form.trainerRequired} onChange={e => set('trainerRequired', e.target.checked)} className="w-4 h-4 accent-violet-600" />
              <label htmlFor="trainerReq" className="text-sm font-bold text-gray-700">Requires a trainer</label>
            </div>
          </div>
        </form>

        <div className="p-5 border-t border-gray-100 shrink-0">
          <button onClick={handleSubmit as any} disabled={saving} className="w-full py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-black hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg shadow-violet-200 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Service'}
          </button>
        </div>
      </div>
      <style>{`
        .label-xs { display:block; font-size:10px; font-weight:900; text-transform:uppercase; letter-spacing:.1em; color:#9ca3af; margin-bottom:6px; }
        .field-input { width:100%; padding:10px 14px; border-radius:12px; border:1px solid #e5e7eb; font-size:14px; outline:none; transition:all .15s; background:#f9fafb; }
        .field-input:focus { border-color:#818cf8; box-shadow:0 0 0 3px rgb(99 102 241/.1); background:white; }
      `}</style>
    </div>
  );
}

export default function ServicesPage() {
  const { userContext } = useUserContext();
  const activeGymPlan = userContext?.roles.find((r: any) => r.plan)?.plan;
  const hasClassScheduling = activeGymPlan?.features?.class_scheduling === true;

  const [services, setServices] = useState<Service[]>([]);
  const { storeFeature } = useStoreFeature();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalService, setModalService] = useState<Partial<Service> | null | false>(false);
  const [gymId, setGymId] = useState<string>('');

  const fetchServices = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const params = new URLSearchParams();
      if (showInactive) params.set('includeInactive', 'true');
      if (search) params.set('search', search);
      const res = await fetch(`${API_BASE_URL}/api/services?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const d = await res.json();
      setServices(d.data || []);
      if ((d.data || []).length > 0 && !gymId) setGymId(d.data[0].gymId);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  };

  // Fetch gymId from user context as fallback
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${API_BASE_URL}/api/gyms`, { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.json()).then(d => { if (d.data?.[0]?.id) setGymId(d.data[0].id); }).catch(() => {});
    });
  }, []);

  useEffect(() => { fetchServices(); }, [search, showInactive]);

  const toggleActive = async (service: Service) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/services/${service.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !service.isActive })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      toast.success(service.isActive ? 'Service deactivated' : 'Service activated');
      fetchServices();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-violet-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Service Offerings</h1>
          <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">Memberships & Add-ons</p>
        </div>

        <button
          onClick={() => setModalService({ gymId })}
          className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-violet-100 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Service</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 outline-none bg-white"
          />
        </div>
        <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer text-sm font-bold text-gray-600 select-none">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="accent-violet-600" />
          Show inactive
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : services.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-3xl bg-violet-50 border border-violet-100 flex items-center justify-center mb-5">
            <ShoppingBag className="w-10 h-10 text-violet-200" />
          </div>
          <p className="font-black text-gray-400 text-lg">No services yet</p>
          <p className="text-sm text-gray-300 mt-1">Add your first service to start selling</p>
          <button onClick={() => setModalService({ gymId })} className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-dashed border-violet-200 text-violet-500 text-sm font-black hover:bg-violet-50 transition-colors">
            <Plus className="w-4 h-4" /> Add Service
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Name</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:table-cell">Category</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden md:table-cell">Type / Class</th>
                  <th className="text-right px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Price</th>
                  <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:table-cell">GST</th>
                  <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                  {storeFeature && <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Store</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {services.map(s => (
                  <tr key={s.id} className={`hover:bg-gray-50/50 transition-colors ${!s.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-4">
                      <p className="font-bold text-gray-900">{s.name}</p>
                      {s.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{s.description}</p>}
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{s.category || '—'}</span>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${
                        s.serviceType === 'CLASS_PACK' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                        s.serviceType === 'CLASS_SESSION' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                        'bg-violet-50 text-violet-600 border-violet-100'
                      }`}>
                        {SERVICE_TYPES.find(t => t.value === (s.serviceType || 'STANDARD'))?.label || s.serviceType}
                      </span>
                      {s.gymClass && <p className="text-[10px] text-gray-400 mt-0.5">{s.gymClass.name}</p>}
                    </td>
                    <td className="px-4 py-4 text-right font-black text-gray-800">₹{Number(s.defaultPrice).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-4 text-center text-xs text-gray-500 hidden sm:table-cell">{s.gstPercent}%</td>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => toggleActive(s)} title={s.isActive ? 'Deactivate' : 'Activate'}>
                        {s.isActive
                          ? <ToggleRight className="w-6 h-6 text-green-500 mx-auto" />
                          : <ToggleLeft className="w-6 h-6 text-gray-300 mx-auto" />}
                      </button>
                    </td>
                    {storeFeature && (
                      <td className="px-4 py-4 text-center">
                        {s.serviceType === 'CLASS_SESSION'
                          ? <span className="text-[10px] text-gray-300" title="Drop-in prices are charged when a member books">—</span>
                          : <SellOnlineSwitch endpoint={`/api/services/${s.id}`} sellOnline={!!s.sellOnline}
                              onChanged={v => setServices(list => list.map(x => x.id === s.id ? { ...x, sellOnline: v } : x))} />}
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <button
                        onClick={() => setModalService(s)}
                        className="p-2 rounded-lg hover:bg-violet-50 hover:text-violet-600 text-gray-400 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalService !== false && (
        <ServiceModal
          service={modalService}
          gymId={gymId}
          hasClassScheduling={hasClassScheduling}
          onClose={() => setModalService(false)}
          onSaved={fetchServices}
        />
      )}
    </div>
  );
}
